import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import type { ContractStatus, Product, ProductQuota } from '../lib/types'
import { resolveProductView, type ProductPage, type ProductView } from '../lib/productView'

/** The one place the portal reads a product's contract status (MS-819 AC2).
 *
 * The status comes from `GET /api/v1/quota`'s `contractStatus`, which the
 * backend derives from the same entitlements resolver Studio's
 * `/api/v1/entitlements` reads (services/entitlements.py; ADR §18) — that
 * route itself is Studio-IAM only, so this is "the entitlements API" as far
 * as the portal session goes (MS-819 Q1, decided by the PO 2026-10-07). It is
 * never inferred from other data (dates, volumes, rows).
 *
 * While the request is pending or has failed there is no status and so no
 * view: `status`/`current` are undefined and `view()` returns undefined. The
 * page renders its title and tabs and a `QueryStateCard` in the tab body —
 * no view is guessed (MS-819 Q7, decided: option A).
 *
 * Same `['quota']` query key every page already shares, so the hook adds no
 * request of its own. */
export function useProductStatus(product: Product) {
  const query = useQuery({
    queryKey: ['quota'],
    queryFn: () => apiFetch<ProductQuota[]>('/api/v1/quota'),
  })
  const quota = query.data

  const statusOf = useCallback(
    (p: Product): ContractStatus | undefined => quota?.find((q) => q.product === p)?.contractStatus,
    [quota]
  )

  /** The view `page` shows for `p` (default: this hook's product), or
   * undefined while the status is unknown. */
  const view = useCallback(
    (page: ProductPage, p: Product = product): ProductView | undefined => {
      const status = statusOf(p)
      return status === undefined ? undefined : resolveProductView(page, status)
    },
    [product, statusOf]
  )

  return {
    /** Every product's quota row (both tabs), once loaded. */
    quota,
    /** This product's quota row, once loaded. */
    current: quota?.find((q) => q.product === product),
    status: statusOf(product),
    view,
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  }
}
